/**
 * Smart Prescription Intake™ — Hub operativo SSN
 * Pagina tenant-wide per gestione flussi ricette + export XML "SmartBooker XML 1.0".
 *
 * Disponibile SOLO per tenant dental.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { toast } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { useIsDentalTenant } from "../../hooks/useIsDentalTenant";
import {
  ScanLine, FileText, FileCode2, FileDown, Loader2, Sparkles,
  CheckCircle2, AlertCircle, Archive, FileWarning, Search,
  Calendar as CalIcon, Filter, RefreshCw, Settings as SettingsIcon,
  ArrowLeft, ChevronRight, Activity, PackageCheck,
} from "lucide-react";

const STATUSES = [
  { v: "all",          label: "Tutti gli stati" },
  { v: "acquired",     label: "Acquisita" },
  { v: "verified",     label: "Verificata" },
  { v: "ready_export", label: "Pronta export" },
  { v: "exported",     label: "Esportata" },
  { v: "archived",     label: "Archiviata" },
];

const STATUS_BADGE = {
  acquired:     "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  verified:     "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  validated:    "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  ready_export: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  exported:     "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  archived:     "bg-slate-200 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300",
};

const STATUS_LABEL = {
  acquired: "Acquisita", verified: "Verificata", validated: "Verificata",
  ready_export: "Pronta export", exported: "Esportata", archived: "Archiviata",
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—";

const PrescriptionsHubPage = () => {
  const navigate = useNavigate();
  const isDental = useIsDentalTenant();

  const [stats, setStats]   = useState(null);
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [exporting, setExp] = useState(false);

  // Filters
  const [status, setStatus] = useState("all");
  const [from, setFrom]     = useState("");
  const [to, setTo]         = useState("");
  const [q, setQ]           = useState("");
  const [quickPeriod, setQuickPeriod] = useState("month");
  const [openSettings, setOpenSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [openPackage, setOpenPackage] = useState(false);

  // Quick period helper
  useEffect(() => {
    const now = new Date();
    const toIso = (d) => d.toISOString().slice(0, 10);
    if (quickPeriod === "today") {
      setFrom(toIso(now)); setTo(toIso(now));
    } else if (quickPeriod === "7d") {
      const a = new Date(now); a.setDate(a.getDate() - 6);
      setFrom(toIso(a)); setTo(toIso(now));
    } else if (quickPeriod === "30d") {
      const a = new Date(now); a.setDate(a.getDate() - 29);
      setFrom(toIso(a)); setTo(toIso(now));
    } else if (quickPeriod === "month") {
      const a = new Date(now.getFullYear(), now.getMonth(), 1);
      setFrom(toIso(a)); setTo(toIso(now));
    } else if (quickPeriod === "year") {
      const a = new Date(now.getFullYear(), 0, 1);
      setFrom(toIso(a)); setTo(toIso(now));
    } else if (quickPeriod === "all") {
      setFrom(""); setTo("");
    }
  }, [quickPeriod]);

  const loadStats = useCallback(async () => {
    if (!isDental) return;
    try {
      const r = await axios.get(`${API}/dental/prescriptions/hub/stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      console.warn("[PrescriptionsHub] stats load failed", e);
    }
  }, [isDental]);

  const loadList = useCallback(async () => {
    if (!isDental) { setLoad(false); return; }
    setLoad(true);
    try {
      const params = {};
      if (status && status !== "all") params.status = status;
      if (from) params.date_from = from;
      if (to)   params.date_to   = to;
      if (q.trim()) params.q = q.trim();
      const r = await axios.get(`${API}/dental/prescriptions/hub/list`,
        { params, withCredentials: true });
      setItems(r.data.prescriptions || []);
    } catch {
      toast.error("Errore caricamento ricette");
    } finally { setLoad(false); }
  }, [isDental, status, from, to, q]);

  const loadSettings = useCallback(async () => {
    if (!isDental) return;
    try {
      const r = await axios.get(`${API}/dental/prescriptions/hub/settings`, { withCredentials: true });
      setSettings(r.data);
    } catch (err) {
      console.warn("[PrescriptionsHub] settings load failed", err);
    }
  }, [isDental]);

  useEffect(() => { loadStats(); loadSettings(); }, [loadStats, loadSettings]);
  useEffect(() => { loadList(); }, [loadList]);

  // Export XML
  const exportXml = async (markExported = true) => {
    if (!items.length) { toast.error("Nessuna ricetta nei filtri correnti"); return; }
    setExp(true);
    try {
      const r = await axios.post(`${API}/dental/prescriptions/hub/export-xml`,
        {
          status:    status === "all" ? null : status,
          date_from: from || null,
          date_to:   to   || null,
          q:         q.trim() || null,
          mark_exported: markExported,
          provider:  "generic",
        },
        { withCredentials: true, responseType: "blob" });

      // Trigger download
      const blob = new Blob([r.data], { type: "application/xml" });
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const filename = `prescrizioni_${new Date().toISOString().slice(0, 10)}.xml`;
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);

      const count    = r.headers["x-rx-count"] || items.length;
      const exported = r.headers["x-rx-exported"] || 0;
      toast.success(`XML generato · ${count} ricette` + (markExported ? ` · ${exported} marcate esportate` : ""));
      loadStats(); loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore export");
    } finally { setExp(false); }
  };

  const saveSettings = async (newFmt) => {
    try {
      const r = await axios.patch(`${API}/dental/prescriptions/hub/settings`,
        { rx_numbering_format: newFmt }, { withCredentials: true });
      setSettings(r.data);
      toast.success("Impostazioni salvate");
    } catch { toast.error("Errore"); }
  };

  if (!isDental) {
    return (
      <AdminLayout>
        <div className="max-w-3xl mx-auto p-8 text-center">
          <FileWarning className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold">Funzionalità riservata agli studi odontoiatrici</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Smart Prescription Intake è disponibile solo per i tenant dental.
          </p>
          <Button onClick={() => navigate("/dashboard")} className="mt-4">Torna alla dashboard</Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-[1600px] mx-auto p-3 sm:p-4 lg:p-6 space-y-5" data-testid="rx-hub-page">
        {/* HERO */}
        <header className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-blue-500/10 via-transparent to-cyan-500/10 dark:from-blue-500/15 dark:to-cyan-500/15 p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="rounded-full mt-1" data-testid="rx-hub-back">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-600 dark:text-blue-400">Smart Prescription Intake™</p>
                <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">Flussi Ricette SSN</h1>
                <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-xl">
                  Dashboard operativa per studi convenzionati. Acquisizione automatica, numerazione progressiva, export XML compatibile con i workflow amministrativi.
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setOpenSettings(true)} className="rounded-full" data-testid="rx-hub-settings-btn">
                <SettingsIcon className="w-3.5 h-3.5 mr-1.5" /> Impostazioni
              </Button>
              <Button variant="outline" onClick={() => { loadStats(); loadList(); }} className="rounded-full" data-testid="rx-hub-refresh">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Aggiorna
              </Button>
              <Button onClick={() => setOpenPackage(true)} className="rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg" data-testid="rx-hub-monthly-btn">
                <PackageCheck className="w-4 h-4 mr-1.5" /> Pacchetto Mensile
              </Button>
              <Button onClick={() => exportXml(true)} disabled={exporting || !items.length} className="rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg" data-testid="rx-hub-export-btn">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileCode2 className="w-4 h-4 mr-1.5" />}
                Esporta XML
              </Button>
            </div>
          </div>
        </header>

        {/* STATS */}
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="rx-hub-stats">
          <StatCard icon={Activity} tone="from-blue-500 to-cyan-500"
            label="Questo mese" value={stats?.this_month ?? "—"} sub={`${stats?.total ?? 0} totale`} tid="stat-month" />
          <StatCard icon={CheckCircle2} tone="from-violet-500 to-indigo-500"
            label="Da verificare" value={stats?.by_status?.acquired ?? "—"} sub="Da controllare" tid="stat-toverify" />
          <StatCard icon={FileText} tone="from-amber-500 to-orange-500"
            label="Pronte export" value={stats?.by_status?.ready_export ?? "—"} sub="In coda XML" tid="stat-ready" />
          <StatCard icon={FileDown} tone="from-emerald-500 to-teal-500"
            label="Esportate" value={stats?.by_status?.exported ?? "—"} sub="Già inviate" tid="stat-exported" />
          <StatCard icon={AlertCircle} tone="from-rose-500 to-pink-500"
            label="Incomplete" value={stats?.incomplete ?? "—"} sub="Dati mancanti" tid="stat-incomplete" />
        </section>

        {/* FILTERS */}
        <section className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 backdrop-blur p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Filtri</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            {/* Quick period */}
            <div className="md:col-span-12">
              <div className="flex gap-1 p-1 rounded-2xl bg-slate-100 dark:bg-white/5 overflow-x-auto">
                {[
                  { v: "today", l: "Oggi" },
                  { v: "7d",    l: "7 giorni" },
                  { v: "30d",   l: "30 giorni" },
                  { v: "month", l: "Questo mese" },
                  { v: "year",  l: "Anno" },
                  { v: "all",   l: "Tutti" },
                ].map(p => (
                  <button key={p.v} onClick={() => setQuickPeriod(p.v)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                      quickPeriod === p.v
                        ? "bg-white dark:bg-slate-900 shadow text-blue-600"
                        : "text-slate-500 hover:bg-white/50"
                    }`}
                    data-testid={`rx-period-${p.v}`}>
                    {p.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Dal</label>
              <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setQuickPeriod("custom"); }} className="rounded-2xl" data-testid="rx-filter-from" />
            </div>
            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Al</label>
              <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setQuickPeriod("custom"); }} className="rounded-2xl" data-testid="rx-filter-to" />
            </div>
            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Stato</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="rounded-2xl" data-testid="rx-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Cerca</label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute top-2.5 left-2.5 text-slate-400" />
                <Input placeholder="Paziente, medico, codice…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-2xl" data-testid="rx-filter-q" />
              </div>
            </div>
          </div>
        </section>

        {/* LIST */}
        <section className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden">
          <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200/60 dark:border-white/10">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">
                {loading ? "Caricamento…" : `${items.length} ricette nei filtri`}
              </h3>
            </div>
          </header>

          {loading ? (
            <div className="p-4 space-y-2">
              {[0,1,2].map(i => <div key={i} className="h-14 rounded-xl bg-slate-100 dark:bg-white/5 animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center" data-testid="rx-hub-empty">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-slate-100 dark:bg-white/5 flex items-center justify-center mb-3">
                <ScanLine className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Nessuna ricetta nei filtri correnti</p>
              <p className="text-xs text-slate-400 mt-1">Acquisisci la prima ricetta dal profilo paziente.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="rx-hub-table">
                <thead className="bg-slate-50 dark:bg-white/5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5">Numero</th>
                    <th className="text-left px-4 py-2.5">Paziente</th>
                    <th className="text-left px-4 py-2.5 hidden md:table-cell">Medico · Prestazione</th>
                    <th className="text-left px-4 py-2.5">Stato</th>
                    <th className="text-left px-4 py-2.5 hidden lg:table-cell">Data</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(rx => (
                    <tr key={rx.rx_id} className="border-t border-slate-100 dark:border-white/5 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-colors" data-testid={`rx-hub-row-${rx.rx_id}`}>
                      <td className="px-4 py-2.5">
                        {rx.rx_number ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-[10px] font-black font-mono" data-testid={`rx-hub-num-${rx.rx_id}`}>
                            #{rx.rx_number}
                          </span>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <button onClick={() => navigate(`/dashboard/client/${rx.client_id}`)}
                          className="text-left hover:underline" data-testid={`rx-hub-client-${rx.rx_id}`}>
                          <p className="font-semibold text-slate-800 dark:text-slate-100 truncate max-w-[180px]">{rx.client_name || "—"}</p>
                          <p className="text-[10px] font-mono text-slate-400 truncate max-w-[180px]">{rx.barcode_value || ""}</p>
                        </button>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell">
                        <p className="text-xs text-slate-700 dark:text-slate-200 truncate max-w-[220px]">{rx.extracted?.doctor_name || "—"}</p>
                        <p className="text-[10px] text-slate-400 truncate max-w-[220px]">{rx.extracted?.prescription || ""}</p>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[rx.status] || STATUS_BADGE.acquired}`}>
                          {STATUS_LABEL[rx.status] || rx.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-slate-500">
                        {fmt(rx.created_at)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/dashboard/client/${rx.client_id}`)} className="h-7 w-7 p-0 rounded-full" data-testid={`rx-hub-open-${rx.rx_id}`}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Recent activity */}
        {stats?.recent && stats.recent.length > 0 && (
          <section className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-violet-500/5 to-blue-500/5 dark:from-violet-500/10 dark:to-blue-500/10 p-4" data-testid="rx-hub-recent">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-violet-500" />
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-600 dark:text-violet-300">Ultime acquisizioni</p>
            </div>
            <ul className="space-y-1.5">
              {stats.recent.map(r => (
                <li key={r.rx_id} className="flex items-center gap-3 text-xs">
                  <span className="font-mono font-black text-[10px] px-1.5 py-0.5 rounded bg-slate-900 text-white dark:bg-white dark:text-slate-900">#{r.rx_number || r.rx_id.slice(0, 6)}</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-200 truncate flex-1">{r.client_name || "—"}</span>
                  <span className="text-slate-400 truncate hidden sm:inline">{r.extracted?.doctor_name || ""}</span>
                  <span className="text-slate-400 text-[10px]">{fmt(r.created_at)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Settings dialog */}
      <Dialog open={openSettings} onOpenChange={setOpenSettings}>
        <DialogContent className="max-w-md" data-testid="rx-hub-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SettingsIcon className="w-4 h-4" /> Impostazioni numerazione
            </DialogTitle>
            <DialogDescription>
              Scegli come numerare automaticamente le ricette acquisite. Il numero viene assegnato in modo atomico, univoco per studio.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <FormatOption
              active={settings?.rx_numbering_format === "yearly"}
              onClick={() => saveSettings("yearly")}
              title="Annuale (raccomandato)"
              sample="2026-000001"
              desc="Si resetta a 000001 ogni anno. Utile per riconciliare flussi mensili/annuali."
              tid="rx-fmt-yearly"
            />
            <FormatOption
              active={settings?.rx_numbering_format === "global"}
              onClick={() => saveSettings("global")}
              title="Progressivo globale"
              sample="RX-000001"
              desc="Mai resettato. Cresce nel tempo, mantenendo un ID storico unico."
              tid="rx-fmt-global"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenSettings(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Monthly Package Wizard */}
      <MonthlyPackageDialog
        open={openPackage}
        onOpenChange={setOpenPackage}
        onDone={() => { loadStats(); loadList(); }}
      />
    </AdminLayout>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, tone, tid }) => (
  <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-4 hover:shadow-lg transition-shadow" data-testid={tid}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
      <div className={`w-7 h-7 rounded-xl bg-gradient-to-br ${tone} flex items-center justify-center`}>
        <Icon className="w-3.5 h-3.5 text-white" />
      </div>
    </div>
    <p className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{value}</p>
    <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>
  </div>
);

const FormatOption = ({ active, onClick, title, sample, desc, tid }) => (
  <button onClick={onClick} data-testid={tid}
    className={`w-full text-left p-3 rounded-2xl border-2 transition-all ${
      active
        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
        : "border-slate-200 dark:border-white/10 hover:border-blue-300"
    }`}>
    <div className="flex items-center justify-between gap-2">
      <p className="font-bold text-sm">{title}</p>
      <span className="font-mono text-[11px] font-black px-2 py-0.5 rounded bg-slate-900 text-white dark:bg-white dark:text-slate-900">
        {sample}
      </span>
    </div>
    <p className="text-[11px] text-slate-500 mt-1">{desc}</p>
  </button>
);

/* ================================================================== */
/* MONTHLY PACKAGE WIZARD                                              */
/* ================================================================== */
const MONTHS_IT = [
  "Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
  "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre",
];

const MonthlyPackageDialog = ({ open, onOpenChange, onDone }) => {
  const now = new Date();
  const [step, setStep]       = useState(1);
  const [year, setYear]       = useState(now.getFullYear());
  const [month, setMonth]     = useState(now.getMonth() + 1);
  const [archivePrev, setArchivePrev] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [running, setRun]     = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setYear(now.getFullYear());
      setMonth(now.getMonth() + 1);
      setArchivePrev(true);
      setPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Load preview when year/month change
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setBusy(true);
      try {
        const r = await axios.get(`${API}/dental/prescriptions/hub/monthly-package/preview`,
          { params: { year, month }, withCredentials: true });
        if (!cancel) setPreview(r.data);
      } catch {
        if (!cancel) setPreview(null);
      } finally { if (!cancel) setBusy(false); }
    })();
    return () => { cancel = true; };
  }, [open, year, month]);

  const yearOptions = useMemo(() => {
    const ys = [];
    const startY = now.getFullYear() - 3;
    for (let y = now.getFullYear(); y >= startY; y--) ys.push(y);
    return ys;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    setRun(true);
    try {
      const r = await axios.post(`${API}/dental/prescriptions/hub/monthly-package`,
        { year, month, mark_exported: true, archive_previous: archivePrev, provider: "generic" },
        { withCredentials: true, responseType: "blob" });

      const blob = new Blob([r.data], { type: "application/xml" });
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `flusso_mensile_${year}-${String(month).padStart(2, "0")}.xml`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);

      const count    = r.headers["x-rx-count"]         || preview?.total || 0;
      const exported = r.headers["x-rx-exported"]      || 0;
      const archived = r.headers["x-rx-prevarchived"]  || 0;
      toast.success(`Flusso ${MONTHS_IT[month-1]} generato · ${count} ricette · ${exported} esportate · ${archived} archiviate`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione flusso");
    } finally { setRun(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="rx-monthly-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <PackageCheck className="w-3.5 h-3.5 text-white" />
            </div>
            Pacchetto Flusso Mensile
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Seleziona il mese e verifica il riepilogo prima di generare il flusso XML completo."
              : "Conferma per generare il file XML, marcare le ricette come esportate e archiviare il mese precedente."}
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-2">
          <StepDot n={1} active={step === 1} done={step > 1} label="Periodo" />
          <div className="flex-1 h-px bg-slate-200 dark:bg-white/10" />
          <StepDot n={2} active={step === 2} done={false} label="Conferma" />
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Mese</label>
                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                  <SelectTrigger className="rounded-2xl" data-testid="rx-monthly-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS_IT.map((m, i) => (
                      <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Anno</label>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                  <SelectTrigger className="rounded-2xl" data-testid="rx-monthly-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview */}
            <div className="rounded-2xl border border-violet-200 dark:border-violet-900/30 bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-violet-900/15 dark:to-fuchsia-900/15 p-4" data-testid="rx-monthly-preview">
              {busy ? (
                <div className="flex items-center gap-2 text-violet-600 text-sm py-4 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Calcolo riepilogo…
                </div>
              ) : !preview ? (
                <p className="text-xs text-slate-500 text-center py-2">Nessuna anteprima disponibile</p>
              ) : (
                <>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700 dark:text-violet-300 mb-3">
                    Riepilogo {MONTHS_IT[month-1]} {year}
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <PreviewKpi label="Totale" value={preview.total} />
                    <PreviewKpi label="Esportabili" value={preview.exportable} tone="text-amber-600" />
                    <PreviewKpi label="Incomplete" value={preview.incomplete} tone="text-rose-500" />
                  </div>
                  <div className="grid grid-cols-5 gap-1 mt-3 text-center">
                    <MiniBadge label="Acq" value={preview.by_status.acquired}     cls="bg-blue-100 text-blue-700" />
                    <MiniBadge label="Ver" value={preview.by_status.verified}     cls="bg-violet-100 text-violet-700" />
                    <MiniBadge label="Pr."  value={preview.by_status.ready_export} cls="bg-amber-100 text-amber-700" />
                    <MiniBadge label="Esp" value={preview.by_status.exported}     cls="bg-emerald-100 text-emerald-700" />
                    <MiniBadge label="Arc" value={preview.by_status.archived}     cls="bg-slate-200 text-slate-600" />
                  </div>
                  {preview.previous_month?.archivable_count > 0 && (
                    <div className="mt-3 rounded-xl bg-white/60 dark:bg-slate-900/30 px-3 py-2 flex items-center gap-2">
                      <Archive className="w-3.5 h-3.5 text-slate-500" />
                      <p className="text-[11px] text-slate-600 dark:text-slate-300">
                        <span className="font-bold">{preview.previous_month.archivable_count}</span> ricette
                        di <span className="font-bold">{MONTHS_IT[preview.previous_month.month-1]} {preview.previous_month.year}</span> verranno archiviate
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-2xl border border-slate-200 dark:border-white/10 hover:border-blue-300 transition-colors" data-testid="rx-monthly-archive-toggle">
              <input
                type="checkbox"
                checked={archivePrev}
                onChange={(e) => setArchivePrev(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-violet-500"
              />
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Archivia mese precedente</p>
                <p className="text-[11px] text-slate-500">Le ricette già esportate del mese precedente vengono spostate in stato "Archiviata".</p>
              </div>
            </label>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-3">
            <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/15 dark:to-teal-900/15 border border-emerald-200 dark:border-emerald-900/30 p-4">
              <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300 mb-2">
                Pronto a generare
              </p>
              <ul className="text-sm space-y-1.5 text-slate-700 dark:text-slate-200">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> File XML <span className="font-mono font-bold">flusso_mensile_{year}-{String(month).padStart(2,"0")}.xml</span></li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {preview.total} ricette di {MONTHS_IT[month-1]} {year} incluse</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {preview.exportable} marcate come "Esportata"</li>
                {archivePrev && preview.previous_month?.archivable_count > 0 && (
                  <li className="flex items-center gap-2"><Archive className="w-3.5 h-3.5 text-emerald-500" /> {preview.previous_month.archivable_count} ricette del mese precedente archiviate</li>
                )}
                <li className="flex items-center gap-2"><FileCode2 className="w-3.5 h-3.5 text-emerald-500" /> Schema SmartBooker-XML 1.0 (provider generic)</li>
              </ul>
            </div>
            {preview.incomplete > 0 && (
              <div className="rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <p className="text-[11px] text-amber-800 dark:text-amber-200">
                  <span className="font-bold">{preview.incomplete} ricette incomplete</span> presenti nel mese: verranno comunque incluse, ma considera di completarle prima dell'invio amministrativo.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running}>Annulla</Button>
              <Button
                onClick={() => setStep(2)}
                disabled={busy || !preview || preview.total === 0}
                className="rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"
                data-testid="rx-monthly-next"
              >
                Avanti <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)} disabled={running} data-testid="rx-monthly-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
              </Button>
              <Button
                onClick={generate}
                disabled={running}
                className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
                data-testid="rx-monthly-generate"
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <PackageCheck className="w-4 h-4 mr-1.5" />}
                Genera flusso XML
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StepDot = ({ n, active, done, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-black ${
      done   ? "bg-emerald-500 text-white" :
      active ? "bg-violet-500 text-white"  :
               "bg-slate-200 dark:bg-white/10 text-slate-500"
    }`}>
      {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : n}
    </div>
    <span className={`text-[11px] font-bold uppercase tracking-wider ${active ? "text-violet-600" : "text-slate-500"}`}>{label}</span>
  </div>
);

const PreviewKpi = ({ label, value, tone = "text-slate-900 dark:text-white" }) => (
  <div className="rounded-xl bg-white/70 dark:bg-slate-900/40 px-3 py-2">
    <p className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
    <p className={`text-2xl font-black ${tone}`}>{value}</p>
  </div>
);

const MiniBadge = ({ label, value, cls }) => (
  <div className={`rounded-lg px-1.5 py-1 ${cls}`}>
    <p className="text-[9px] font-bold uppercase">{label}</p>
    <p className="text-sm font-black">{value}</p>
  </div>
);

export default PrescriptionsHubPage;
